import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const sectionStyle = { marginBottom: '48px' };
  const h2Style = { fontSize: '28px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '16px', borderBottom: '2px solid #C9A961', paddingBottom: '8px' };
  const h3Style = { fontSize: '20px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '12px', marginTop: '24px' };
  const h4Style = { fontSize: '17px', fontWeight: '600', color: '#232e19', marginBottom: '8px', marginTop: '20px' };
  const pStyle = { marginBottom: '16px' };
  const ulStyle = { marginLeft: '24px', marginBottom: '16px', lineHeight: '1.8' };
  const calloutStyle = { marginBottom: '48px', background: 'rgba(201, 169, 97, 0.05)', padding: '32px', border: '2px solid #C9A961' };

  return (
    <div style={{ minHeight: '100vh', background: '#fffef2' }}>
      <Helmet>
        <title>Privacy Policy | Privé Wealth Management</title>
        <meta name="description" content="Privacy Policy for Privé Wealth Management, LLC. Learn how we collect, use, and protect your personal and financial information, including SMS communications." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.privewealthmanagement.com/privacy" />
        <meta property="og:title" content="Privacy Policy | Privé Wealth Management" />
        <meta property="og:description" content="Privacy Policy for Privé Wealth Management, LLC. Learn how we collect, use, and protect your personal and financial information." />
        <meta property="og:url" content="https://www.privewealthmanagement.com/privacy" />
      </Helmet>

      {/* Header */}
      <header style={{ background: '#232e19', borderBottom: '1px solid rgba(201, 169, 97, 0.3)', padding: '20px 0', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img
              src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/0u8dfr1l_Prive%CC%81%20WM%20Main%20Logo%202.png"
              alt="Privé Wealth Management"
              style={{ height: '90px', width: 'auto' }}
            />
          </div>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'transparent',
              border: '1px solid #C9A961',
              color: '#C9A961',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
            }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '80px 60px 120px' }}>
        <h1 style={{ fontSize: '48px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '16px', textAlign: 'center' }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: '16px', color: '#666', textAlign: 'center', marginBottom: '8px' }}>
          Privé Wealth Management, LLC
        </p>
        <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '8px' }}>
          Fee-Only Registered Investment Adviser
        </p>
        <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '8px' }}>
          State of Georgia | CRD #339705
        </p>
        <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '8px' }}>
          Version 1.1 | April 2026 | Classification: Client Document
        </p>
        <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '60px' }}>
          Effective Date: January 16, 2026 | SMS Section Effective: May 12, 2026 | Last Reviewed: May 2026
        </p>

        <div style={{ fontSize: '16px', lineHeight: '1.8', color: '#333' }}>

          {/* Our Commitment */}
          <section style={calloutStyle}>
            <h2 style={{ fontSize: '28px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '16px' }}>
              OUR COMMITMENT TO PROTECTING YOUR PRIVACY
            </h2>
            <p style={pStyle}>
              Privé Wealth Management, LLC ("Privé," "we," "our," or "us") is committed to safeguarding the confidential information of our clients. We recognize that you have entrusted us with personal and financial information, and we take seriously our obligation to keep that information secure and confidential.
            </p>
            <p style={pStyle}>
              In the course of providing financial organization, insurance planning, comprehensive financial planning, ongoing advisory retainer, investment management, and Privé Heritage multi-family office services, we collect, use, and protect your personal information as described in this policy. This Privacy Policy describes what information we collect about you, how we use that information, whether we disclose that information to anyone, how we protect your information, and our SMS/text messaging practices.
            </p>
            <p style={{ marginBottom: 0, fontWeight: 'bold' }}>
              We do not sell client information to third parties.
            </p>
          </section>

          {/* Information We Collect */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>INFORMATION WE COLLECT</h2>

            <h3 style={h3Style}>Information You Provide to Us</h3>

            <h4 style={h4Style}>Personal Identifying Information</h4>
            <ul style={ulStyle}>
              <li>Name, address, telephone number, email address</li>
              <li>Mobile phone number (if you opt into SMS communications)</li>
              <li>Social Security number or Tax Identification Number</li>
              <li>Date of birth</li>
              <li>Government-issued identification (driver's license, passport)</li>
            </ul>

            <h4 style={h4Style}>Financial Information</h4>
            <ul style={ulStyle}>
              <li>Income and employment information</li>
              <li>Assets and liabilities</li>
              <li>Net worth</li>
              <li>Bank account information</li>
              <li>Credit card information (if used for fee payments)</li>
              <li>Investment account information</li>
              <li>Tax information</li>
              <li>Insurance policies</li>
            </ul>

            <h4 style={h4Style}>Family and Personal Information</h4>
            <ul style={ulStyle}>
              <li>Marital status and family information</li>
              <li>Beneficiary designations</li>
              <li>Estate planning documents (wills, trusts, powers of attorney)</li>
              <li>Health information (if relevant to insurance planning or financial planning)</li>
            </ul>

            <h4 style={h4Style}>Goals and Preferences</h4>
            <ul style={ulStyle}>
              <li>Investment objectives and risk tolerance</li>
              <li>Financial goals and concerns</li>
              <li>Time horizon</li>
              <li>Restrictions or preferences for investments</li>
              <li>Communication preferences (including SMS opt-in status)</li>
            </ul>

            <h3 style={h3Style}>Information We Receive from Your Transactions</h3>
            <ul style={ulStyle}>
              <li>Account balances and positions</li>
              <li>Transaction history (purchases, sales, deposits, withdrawals)</li>
              <li>Investment performance</li>
              <li>Fees paid</li>
            </ul>

            <h3 style={h3Style}>Information We Receive from Third Parties</h3>
            <p style={{ marginBottom: '12px' }}>With your authorization, we may receive information from:</p>
            <ul style={ulStyle}>
              <li>The qualified custodian holding your accounts</li>
              <li>Your other professional advisers (accountants, attorneys, other financial advisers)</li>
              <li>Credit bureaus (if applicable)</li>
              <li>Insurance carriers</li>
              <li>Employers (for retirement plan information)</li>
              <li>Communications service providers (used to facilitate phone services)</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>HOW WE USE YOUR INFORMATION</h2>
            <p style={pStyle}>
              We use your information only for legitimate business purposes in connection with providing advisory services, including:
            </p>

            <h4 style={h4Style}>Providing Financial Advice</h4>
            <ul style={ulStyle}>
              <li>Analyzing your financial situation</li>
              <li>Developing financial plans and investment strategies</li>
              <li>Making investment recommendations</li>
              <li>Monitoring and managing your accounts</li>
            </ul>

            <h4 style={h4Style}>Servicing Your Accounts</h4>
            <ul style={ulStyle}>
              <li>Processing transactions</li>
              <li>Calculating and billing fees</li>
              <li>Providing account statements and performance reports</li>
              <li>Responding to your inquiries</li>
            </ul>

            <h4 style={h4Style}>Communicating with You</h4>
            <ul style={ulStyle}>
              <li>Sending appointment reminders (via email, phone, or SMS if you opt in)</li>
              <li>Providing account alerts and notifications (via email, phone, or SMS if you opt in)</li>
              <li>Responding to your questions and requests</li>
              <li>Sending important service announcements</li>
            </ul>

            <h4 style={h4Style}>Complying with Legal Requirements</h4>
            <ul style={ulStyle}>
              <li>Verifying your identity (anti-money laundering compliance)</li>
              <li>Filing regulatory reports</li>
              <li>Responding to subpoenas or legal process</li>
              <li>Maintaining required books and records</li>
            </ul>

            <h4 style={h4Style}>Operating Our Business</h4>
            <ul style={ulStyle}>
              <li>Managing risk</li>
              <li>Protecting against fraud</li>
              <li>Resolving disputes or complaints</li>
              <li>Auditing and quality control</li>
            </ul>

            <h4 style={h4Style}>Improving Our Services</h4>
            <ul style={ulStyle}>
              <li>Understanding client needs and preferences</li>
              <li>Developing new services</li>
              <li>Analyzing the effectiveness of our services</li>
            </ul>

            <p style={{ marginTop: '24px', fontWeight: 'bold' }}>
              We do not use your information for unrelated marketing purposes or sell your information to third parties.
            </p>
          </section>

          {/* SMS / Text Messaging */}
          <section style={calloutStyle}>
            <h2 style={{ fontSize: '28px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '8px' }}>
              SMS/TEXT MESSAGING TERMS AND CONDITIONS
            </h2>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '24px', fontStyle: 'italic' }}>
              SMS Communications Section · Effective: May 12, 2026
            </p>

            <h3 style={h3Style}>Overview</h3>
            <p style={pStyle}>
              Privé Wealth Management offers optional SMS (text message) communications to clients, prospective clients, professional partners, and other business contacts who choose to receive messages via text. SMS communications are intended to support our professional relationships and may include appointment reminders, confirmations, service-related notifications, and other brief, non-sensitive, business-related communications. SMS messages are sent by Privé Wealth Management using secure business communications infrastructure.
            </p>
            <p style={pStyle}>
              SMS communications are entirely optional. You are not required to opt into SMS to receive our advisory services. You may choose to communicate with us via email and phone calls only.
            </p>

            <h3 style={h3Style}>Mobile Information Sharing</h3>
            <p style={pStyle}>
              Mobile opt-in information and SMS consent data will not be shared with third parties or affiliates for any purpose. Privé Wealth Management does not share, sell, rent, lease, or disclose mobile opt-in information, SMS consent data, or mobile phone numbers collected for SMS purposes with any third parties. Categories of mobile information excluded from sharing include text messaging originator opt-in data and consent. This information will not be shared with any third parties. Mobile phone numbers collected for SMS purposes are used solely to deliver text messages from Privé Wealth Management.
            </p>

            <h3 style={h3Style}>Consent and Opt-In</h3>
            <p style={pStyle}>
              By consenting to receive SMS messages from Privé Wealth Management, you agree to these SMS/Text Messaging Terms and Conditions and to our Privacy Policy, available at <a href="/privacy" style={{ color: '#C9A961' }}>https://www.privewealthmanagement.com/privacy</a>. Privé Wealth Management sends SMS messages only to individuals who have provided prior express consent to receive text communications.
            </p>
            <p style={{ marginBottom: '12px' }}>You may provide consent to receive SMS messages through any of the following methods:</p>
            <ul style={ulStyle}>
              <li><strong>Web Form Submission:</strong> Entering your mobile number on the contact form at https://www.privewealthmanagement.com/contact-us and selecting an optional SMS consent checkbox. Consent to receive SMS messages is not required to submit the form; SMS consent is separate from any general contact or service consent</li>
              <li><strong>Verbal Consent:</strong> Providing verbal consent during a phone call or in-person conversation with Privé Wealth Management staff, where consent is documented in our records with date, time, and the consenting party's name</li>
            </ul>
            <p style={pStyle}>
              We do not send SMS messages without prior express consent provided through one of these two methods. Privé Wealth Management may also maintain other forms of documented consent obtained through established business or onboarding processes; these are recorded internally as supporting consent records.
            </p>

            <h3 style={h3Style}>Types of SMS Messages You May Receive</h3>
            <p style={{ marginBottom: '12px' }}>If you opt into SMS communications, you may receive the following types of messages:</p>
            <ul style={ulStyle}>
              <li><strong>Appointment Reminders:</strong> Reminders of upcoming meetings and calls</li>
              <li><strong>Account Alerts:</strong> Notifications about your investment accounts (if you request them)</li>
              <li><strong>Service Notifications:</strong> Important updates about our services or your account</li>
              <li><strong>Security Codes:</strong> Two-factor authentication codes for secure account access</li>
              <li><strong>Conversations:</strong> Text-based conversations between you and your adviser</li>
            </ul>
            <p style={pStyle}>
              We do not send marketing or promotional messages via SMS unless you specifically opt into marketing communications separately.
            </p>

            <h3 style={h3Style}>Message Frequency</h3>
            <p style={pStyle}>
              Message frequency varies based on your level of engagement with Privé Wealth Management. You will typically receive no more than 20 SMS messages per month, though message volume may temporarily increase during active client onboarding, financial planning engagements, Heritage multi-family office active engagements, insurance application periods, year-end tax and planning windows, or other periods of intensive service delivery.
            </p>

            <h3 style={h3Style}>Message and Data Rates</h3>
            <p style={pStyle}>
              Message and data rates may apply. Standard text messaging rates from your mobile carrier will apply to SMS messages sent and received. We do not charge additional fees for SMS communications, but your wireless carrier may charge you according to your mobile plan. We are not responsible for charges from your mobile carrier.
            </p>

            <h3 style={h3Style}>Supported Carriers</h3>
            <p style={pStyle}>
              SMS services are supported by most major U.S. wireless carriers, subject to carrier availability and network conditions. Message delivery cannot be guaranteed in all circumstances. If you experience issues receiving messages, please contact your mobile carrier or contact Privé Wealth Management for assistance.
            </p>

            <h3 style={h3Style}>Opt-Out</h3>
            <p style={pStyle}>
              You may opt out of SMS messages at any time by replying <strong>STOP</strong> to any message you receive from Privé Wealth Management. After opting out, you will have opted out of the Privé Wealth Management SMS service and will not receive further messages from this Privé Wealth Management number unless you re-enroll. You may opt back in at any time by replying <strong>START</strong> to the same number, or by contacting Privé Wealth Management directly using the support information below. Opting out of SMS will not affect your ability to receive our advisory services through other communication channels.
            </p>
            <p style={{ marginBottom: '16px', fontStyle: 'italic', fontSize: '15px' }}>
              Note: For technical reasons, the keywords STOP, QUIT, END, REVOKE, OPT OUT, CANCEL, and UNSUBSCRIBE will all be recognized by the underlying messaging system as opt-out requests. Replying STOP is the recommended and supported method.
            </p>

            <h3 style={h3Style}>Customer Support and Customer Care Contact</h3>
            <p style={pStyle}>
              For more assistance with the Privé Wealth Management SMS service, reply <strong>HELP</strong> to any SMS message, call (470) 737-4131, or email info@privewealthmanagement.com.
            </p>
            <p style={pStyle}>
              Msg frequency varies. Reply STOP to opt out from messages from this Privé Wealth Management number. Msg &amp; data rates may apply.
            </p>

            <h3 style={h3Style}>Security and Privacy of SMS Communications</h3>

            <h4 style={h4Style}>Important Security Information</h4>
            <ul style={ulStyle}>
              <li><strong>SMS is not encrypted:</strong> Text messages are not encrypted by mobile carriers and should not be considered a secure form of communication</li>
              <li><strong>No sensitive information via SMS:</strong> We will not send sensitive financial information, account numbers, Social Security numbers, or passwords via text message</li>
              <li><strong>Device security is your responsibility:</strong> You are responsible for securing your mobile device with a passcode or biometric authentication</li>
              <li><strong>Risk of interception:</strong> Text messages can potentially be intercepted or accessed by others who have access to your phone</li>
              <li><strong>Lost or stolen phones:</strong> If your phone is lost or stolen, immediately contact us to disable SMS communications</li>
            </ul>

            <h4 style={h4Style}>What We Will and Won't Send via SMS</h4>
            <p style={{ marginBottom: '8px' }}><strong>We WILL send via SMS:</strong></p>
            <ul style={ulStyle}>
              <li>Appointment reminders (date, time, general topic)</li>
              <li>General account alerts ("Your quarterly statement is available")</li>
              <li>Service announcements ("Our office will be closed on [date]")</li>
              <li>Two-factor authentication codes</li>
              <li>Responses to your non-sensitive questions</li>
            </ul>
            <p style={{ marginBottom: '8px' }}><strong>We WILL NOT send via SMS:</strong></p>
            <ul style={ulStyle}>
              <li>Account numbers or balances</li>
              <li>Social Security numbers</li>
              <li>Passwords or security questions</li>
              <li>Detailed financial information</li>
              <li>Recommendations to buy or sell specific securities</li>
              <li>Sensitive personal information</li>
            </ul>
            <p style={pStyle}>
              For sensitive communications, we will use secure email, phone calls, or our secure client portal.
            </p>

            <h3 style={h3Style}>Consent and Agreement</h3>
            <p style={pStyle}>
              By opting into SMS communications from Privé Wealth Management, you consent to receive SMS messages at the mobile number you provided, confirm that you are the owner or authorized user of the mobile number, agree to notify us if your mobile number changes, understand that message and data rates may apply, understand that SMS is not encrypted and carries security risks, understand you can opt out at any time by replying STOP, and agree to these SMS/Text Messaging Terms and Conditions.
            </p>
          </section>

          {/* Sharing */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>SHARING YOUR INFORMATION WITH THIRD PARTIES</h2>
            <p style={pStyle}>
              We may share your information with third parties only as described below. <strong>We do not sell your information.</strong>
            </p>

            <h3 style={h3Style}>Service Providers</h3>
            <p style={{ marginBottom: '12px' }}>
              We may disclose your information to companies that perform services on our behalf, including:
            </p>
            <ul style={ulStyle}>
              <li><strong>Custodian:</strong> The qualified custodian, which holds your investment accounts and executes transactions</li>
              <li><strong>Financial Planning Software:</strong> Software providers used to develop and deliver your financial plan</li>
              <li><strong>Client Data Gathering:</strong> Form platforms used to collect your financial information securely during onboarding and plan updates</li>
              <li><strong>Financial Aggregation:</strong> Cash flow tracking and account aggregation platforms used in financial organization services</li>
              <li><strong>Communications Providers:</strong> Communications service providers, which facilitate phone services</li>
              <li><strong>Payment Processing:</strong> Payment platforms used to process advisory fee payments</li>
              <li><strong>E-Signature Providers:</strong> Electronic signature platforms used for agreement execution</li>
              <li><strong>Compliance Management:</strong> Compliance platforms used to maintain regulatory records and marketing archives</li>
              <li><strong>Professional Advisers:</strong> Our attorneys, accountants, or compliance consultants who assist us in providing services or operating our business</li>
              <li><strong>Third-Party Money Managers:</strong> Investment management firms that provide model portfolios or sub-advisory services for your account</li>
              <li><strong>Insurance Carriers:</strong> When you apply for insurance products through a licensed agent or broker of your choosing based on our advisory recommendations, we may share information necessary to support the application process at your direction</li>
            </ul>
            <p style={pStyle}>
              When we share information with service providers, we require contractual commitments to protect your information and to notify us within 72 hours of discovering any security incident affecting your data. We share only the information necessary for them to provide their services, enter into agreements requiring them to protect the confidentiality of your information, and prohibit them from using your information for their own marketing purposes.
            </p>

            <h3 style={h3Style}>Other Disclosures</h3>
            <p style={{ marginBottom: '12px' }}>We may also disclose your information:</p>
            <ul style={ulStyle}>
              <li><strong>At Your Direction:</strong> When you authorize us to share information (e.g., with your accountant or attorney)</li>
              <li><strong>To Protect Our Rights:</strong> To enforce our agreements, collect fees owed, or protect our legal rights</li>
              <li><strong>As Required by Law:</strong> To comply with laws, regulations, court orders, subpoenas, or other legal process</li>
              <li><strong>To Regulatory Authorities:</strong> To respond to examinations or inquiries from securities regulators, state insurance departments, or other authorities</li>
              <li><strong>In Corporate Transactions:</strong> In the event of a merger, acquisition, or sale of our business (subject to confidentiality protections)</li>
            </ul>

            <h3 style={h3Style}>What We Do NOT Do</h3>
            <ul style={ulStyle}>
              <li>We do <strong>NOT</strong> sell your information to third parties</li>
              <li>We do <strong>NOT</strong> share your information with unaffiliated third parties for their marketing purposes</li>
              <li>We do <strong>NOT</strong> share your information with affiliates for their marketing purposes (we have no affiliates)</li>
            </ul>
            <p style={pStyle}>
              Because we do not share your information for marketing purposes, you do not have a right to "opt out" of such sharing.
            </p>
          </section>

          {/* Protection */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>HOW WE PROTECT YOUR INFORMATION</h2>
            <p style={pStyle}>
              We maintain physical, electronic, and procedural safeguards to protect your information from unauthorized access or use.
            </p>

            <h4 style={h4Style}>Physical Safeguards</h4>
            <ul style={ulStyle}>
              <li>Secure storage of paper records in locked cabinets</li>
              <li>Controlled access to our office</li>
              <li>Secure disposal of records (shredding) when no longer needed</li>
              <li>Building security measures</li>
            </ul>

            <h4 style={h4Style}>Electronic Safeguards</h4>
            <ul style={ulStyle}>
              <li>Firewall protection and antivirus software</li>
              <li>Encryption of sensitive data (both stored data and data in transit)</li>
              <li>Strong password policies and authentication</li>
              <li>Secure email for transmission of sensitive information</li>
              <li>Regular software updates and security patches</li>
              <li>Secure Wi-Fi networks with encryption</li>
              <li>Multi-factor authentication where available</li>
              <li>Device encryption for laptops, phones, and tablets</li>
              <li>Regular data backups</li>
              <li>Secure cloud storage with reputable providers</li>
            </ul>

            <h4 style={h4Style}>Procedural Safeguards</h4>
            <ul style={ulStyle}>
              <li>Access to client information limited to personnel who need it to provide services</li>
              <li>Confidentiality agreements signed by all personnel</li>
              <li>Regular training on data security and privacy</li>
              <li>Written information security policies</li>
              <li>Vendor due diligence and oversight</li>
              <li>Incident response procedures</li>
              <li>Regular review and updating of security measures</li>
            </ul>

            <h3 style={h3Style}>Your Role in Protecting Your Information</h3>
            <p style={{ marginBottom: '12px' }}>
              While we take extensive measures to protect your information, you also play an important role:
            </p>
            <ul style={ulStyle}>
              <li><strong>Protect your passwords:</strong> Use strong passwords for your custodian accounts and online portals; do not share passwords</li>
              <li><strong>Secure your mobile device:</strong> Use a passcode or biometric lock on your phone; enable remote wipe capability</li>
              <li><strong>Be cautious with communications:</strong> Do not send sensitive information via unencrypted email or SMS; use secure methods we provide</li>
              <li><strong>Verify communications:</strong> Be suspicious of emails, calls, or texts claiming to be from us or your custodian asking for sensitive information; verify by calling us directly</li>
              <li><strong>Review statements:</strong> Regularly review your account statements for unauthorized activity</li>
              <li><strong>Report suspicious activity:</strong> Immediately report any suspected security incidents or fraudulent activity</li>
              <li><strong>Use secure networks:</strong> Avoid accessing financial accounts on public Wi-Fi networks</li>
              <li><strong>Keep software updated:</strong> Ensure your devices have the latest security updates</li>
            </ul>
          </section>

          {/* Data Breach */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>DATA BREACH NOTIFICATION</h2>
            <p style={pStyle}>
              In the unlikely event that your information is compromised in a data breach, we will notify you within 30 days of determining that unauthorized access to or use of your sensitive personal information has occurred or is reasonably likely to have occurred, as required by amended Regulation S-P. Our notification will include a description of what happened, types of information that were compromised, steps we have taken to address the breach, steps you should take to protect yourself, and how to contact us with questions. We will also notify appropriate regulatory authorities and law enforcement as required by law.
            </p>
          </section>

          {/* Rights */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>YOUR RIGHTS AND CHOICES</h2>
            <p style={{ marginBottom: '12px' }}>You have the right to:</p>
            <ul style={ulStyle}>
              <li><strong>Access Your Information:</strong> Request to see the personal information we have about you</li>
              <li><strong>Correct Your Information:</strong> Request that we correct inaccurate information</li>
              <li><strong>Limit Information Sharing:</strong> While we do not share your information for marketing purposes, you may limit our sharing with service providers in some circumstances, though this may limit our ability to provide services</li>
              <li><strong>Opt Out of SMS:</strong> You may opt out of the Privé Wealth Management SMS service at any time by replying STOP to any message you receive from Privé Wealth Management or by contacting us</li>
              <li><strong>Close Your Account:</strong> Terminate our advisory relationship at any time as provided in your Client Advisory Agreement</li>
            </ul>
            <p style={pStyle}>
              To exercise these rights, please contact us using the information at the end of this policy.
            </p>
          </section>

          {/* Former Clients */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>INFORMATION ABOUT FORMER CLIENTS</h2>
            <p style={pStyle}>
              If you close your account or terminate our advisory relationship, we will continue to protect your information as described in this Privacy Policy. We retain information about former clients as required by law and regulation (typically for a minimum of 7 years per firm policy (exceeding SEC and state securities regulations)) and continue to safeguard it according to our information security policies. We do not share information about former clients except as permitted for current clients (to service providers, as required by law, etc.).
            </p>
          </section>

          {/* California */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>SPECIAL NOTICE FOR CALIFORNIA RESIDENTS</h2>
            <p style={{ marginBottom: '12px' }}>
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
            </p>
            <ul style={ulStyle}>
              <li>Right to know what personal information we collect, use, and share</li>
              <li>Right to request deletion of your personal information (subject to exceptions for information needed to provide services or comply with legal obligations)</li>
              <li>Right to opt out of "sale" of personal information (note: we do not sell personal information)</li>
              <li>Right to non-discrimination for exercising your privacy rights</li>
            </ul>
            <p style={pStyle}>To exercise these rights, please contact us using the information below.</p>
            <p style={{ marginBottom: 0, fontStyle: 'italic' }}>
              <strong>California Notice at Collection:</strong> We collect the categories of personal information described in this Privacy Policy for the business purposes described. We do not sell personal information.
            </p>
          </section>

          {/* Insurance */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>SPECIAL NOTICE FOR INSURANCE CLIENTS</h2>
            <p style={pStyle}>
              For clients to whom we provide insurance planning advisory services, this Privacy Policy also serves as the insurance privacy notice required by Georgia insurance regulations. Privé Wealth Management provides fee-only insurance advisory services. We do not sell insurance products and do not receive commissions from insurance carriers. When you purchase insurance products through a licensed agent or broker of your choosing based on our advisory recommendations, we may share information with insurance carriers at your direction as necessary to support the application process. Insurance carriers have their own privacy policies governing their use of your information, and you should review those policies.
            </p>
          </section>

          {/* Children */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>CHILDREN'S PRIVACY</h2>
            <p style={pStyle}>
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe we have collected information about a child, please contact us.
            </p>
          </section>

          {/* Changes */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>CHANGES TO THIS PRIVACY POLICY</h2>
            <p style={pStyle}>
              We reserve the right to modify this Privacy Policy at any time. If we make material changes, we will provide you with a revised Privacy Policy and, if required by law, seek your consent. We will provide an updated Privacy Policy when we make material changes, at least annually (if required based on our information sharing practices), and upon request.
            </p>
          </section>

          {/* Questions or Complaints */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>QUESTIONS OR COMPLAINTS</h2>
            <p style={{ marginBottom: '12px' }}>
              If you have questions about this Privacy Policy or concerns about our privacy practices, please contact us:
            </p>
            <div style={{ background: '#f6f5e8', padding: '24px', marginTop: '16px', marginBottom: '24px' }}>
              <p style={{ marginBottom: '8px' }}><strong>Privé Wealth Management, LLC</strong></p>
              <p style={{ marginBottom: '8px' }}>Philippe-Joseph Eustache, Chief Compliance Officer</p>
              <p style={{ marginBottom: '8px' }}>1702 Sweet Barley Way, Grayson, GA 30017</p>
              <p style={{ marginBottom: '8px' }}>Phone: (470) 737-4131</p>
              <p style={{ marginBottom: '8px' }}>Email: peustache@privewealthmanagement.com</p>
              <p>Website: www.privewealthmanagement.com</p>
            </div>
            <p style={{ marginBottom: '16px' }}>
              We will respond to your inquiry promptly. If you believe we have violated your privacy rights or have a complaint about our privacy practices, you may also contact:
            </p>

            <div style={{ background: '#f6f5e8', padding: '24px', marginBottom: '16px' }}>
              <p style={{ marginBottom: '8px' }}><strong>Georgia Securities Division</strong></p>
              <p style={{ marginBottom: '8px' }}>2 Martin Luther King Jr. Drive SE, Suite 802, West Tower, Atlanta, GA 30334</p>
              <p style={{ marginBottom: 0 }}>Phone: (404) 656-3920</p>
            </div>

            <div style={{ background: '#f6f5e8', padding: '24px', marginBottom: '16px' }}>
              <p style={{ marginBottom: '8px' }}><strong>Securities and Exchange Commission</strong></p>
              <p style={{ marginBottom: '8px' }}>100 F Street, NE, Washington, DC 20549</p>
              <p style={{ marginBottom: 0 }}>Phone: (202) 551-6551 | Website: www.sec.gov</p>
            </div>

            <div style={{ background: '#f6f5e8', padding: '24px' }}>
              <p style={{ marginBottom: '8px' }}><strong>Georgia Department of Insurance</strong> (for insurance-related complaints)</p>
              <p style={{ marginBottom: '8px' }}>2 Martin Luther King Jr. Drive, Suite 704, West Tower, Atlanta, GA 30334</p>
              <p style={{ marginBottom: 0 }}>Phone: (404) 656-2070</p>
            </div>
          </section>

          {/* Annual Notice */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>ANNUAL PRIVACY NOTICE DELIVERY</h2>
            <p style={pStyle}>
              We provide this Privacy Policy to all clients when you first become a client, annually if required based on our information sharing practices (currently, annual delivery is not required because we do not share information for marketing purposes), when we make material changes to the policy, and upon request at any time. You may request a copy of this Privacy Policy at any time by contacting us at the phone number or email address above.
            </p>
          </section>

          <div style={{ textAlign: 'center', padding: '32px 0', borderTop: '2px solid #C9A961', marginTop: '48px' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              Effective Date: January 16, 2026 | SMS Section Effective: May 12, 2026 | Last Reviewed: May 2026
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              © 2026 Privé Wealth Management, LLC. All rights reserved.
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              Privé Wealth Management, LLC | 1702 Sweet Barley Way, Grayson, GA 30017
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
              (470) 737-4131 | info@privewealthmanagement.com | www.privewealthmanagement.com
            </p>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>CRD #339705</p>
            <p style={{ fontSize: '14px', color: '#C9A961', fontStyle: 'italic' }}>
              "Where Financial Freedom Takes Flight"
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: '#f6f5e8', padding: '40px 0', borderTop: '1px solid #bcbbb4' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 60px', textAlign: 'center' }}>
          <p style={{ fontSize: '14px', color: '#666' }}>© 2026 Privé Wealth Management, LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
